import type { MediaFormat } from '@prisma/client'
import type { SerializeFrom } from '@remix-run/node'
import { useEffect, useRef, useState } from 'react'

import { Media } from '~/components/Media/Media'
import type { AmendableAttachmentType } from '~/contexts/MediaManagerContext'
import type { AttachmentWithMedia } from '~/models/attachment.server'
import { cn } from '~/utils/classnames'

interface ProductMediaGalleryProps {
  format: MediaFormat
  attachments: SerializeFrom<AttachmentWithMedia>[] | AmendableAttachmentType[]
  className?: string
}

export const ProductMediaGallery = ({
  format,
  attachments,
  className,
}: ProductMediaGalleryProps) => {
  const scrollContainerRef = useRef<HTMLOListElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const items = [...container.querySelectorAll('li')] as HTMLLIElement[]

    const options = {
      threshold: 0.75,
      rootMargin: '0px 0px 0px 0px',
    }

    const intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = items.indexOf(entry.target as HTMLLIElement)
          setActiveIndex(index)
        }
      })
    }, options)

    items.forEach((item) => intersectionObserver.observe(item))

    return () => {
      intersectionObserver.disconnect()
    }
  }, [attachments])

  return (
    <div className={cn('size-full', className)}>
      <ol
        ref={scrollContainerRef}
        className={cn(
          'hide-scrollbar flex h-full snap-mandatory @max-lg:snap-x @max-lg:overflow-x-auto @max-lg:overflow-y-hidden @lg:snap-y @lg:flex-col @lg:gap-2 @lg:overflow-y-auto @lg:rounded-md'
        )}
      >
        {attachments.map(({ media }, index) => (
          <li
            key={index}
            className={cn(
              'w-full shrink-0 snap-center overflow-hidden @lg:rounded-md',
              'transition-[aspect-ratio] duration-400 ease-out motion-reduce:transition-none',
              format === 'square' && 'aspect-square',
              format === 'landscape' && 'aspect-[4/3]',
              format === 'portrait' && 'aspect-[3/4]'
            )}
          >
            <Media
              kind={media.kind}
              src={media.src}
              sizes={{
                default: '50vw',
                xl: '50vw',
                lg: '50vw',
                md: '100vw',
                sm: '100vw',
                xs: '100vw',
              }}
              className="size-full object-cover"
            />
          </li>
        ))}
      </ol>
      <div
        inert
        className={cn(
          'absolute bottom-0 left-0 flex w-full items-center justify-center pt-32 pb-4 @lg:hidden',
          'bg-gradient-to-t from-black/48 via-black/12 via-50% to-transparent'
        )}
      >
        <Stepper length={attachments.length} index={activeIndex} />
      </div>
    </div>
  )
}

interface StepperProps {
  length: number
  index: number
  className?: string
}

const Stepper = ({ length, index: activeIndex, className }: StepperProps) => {
  if (length < 2) return null

  return (
    <div className={cn('flex w-full items-center justify-center', className)}>
      {Array.from({ length }).map((_, index) => {
        return (
          <div key={index} className="px-1 py-1.5">
            <div
              className={cn(
                'bg-highlight h-1.5 w-full rounded-full',
                'transition-width duration-300 motion-reduce:transition-none',
                index > activeIndex && 'opacity-32',
                index === activeIndex ? 'w-7' : 'w-1.5'
              )}
            />
          </div>
        )
      })}
    </div>
  )
}
